import React, { useEffect, useRef, useState } from 'react';
import { Box, Slide, TextField, Button, IconButton, Fade, Tooltip, Chip, Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import helpdeskIcon from '../Images/helpdesk.png';
import Cookies from 'js-cookie';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';

const CHAT_STORAGE_KEY = 'helpdeskChatOpen';
const SESSION_KEY = 'helpdeskSessionId';
const MESSAGES_STORAGE_KEY = 'helpdeskMessages';
const FLOW_ENDPOINT = 'https://flowise.hyperminds.tech/api/v1/prediction/fd61864b-d032-4a18-adf0-2a2c245dfcd7';

// Format timestamp
function formatTime(date) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

// --- minimal session id (no crypto/uuid) ---
function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

// --- try to grab a connector type from a free-text query ---
function extractConnectorSelection(text) {
  const s = (text || '').trim();
  if (!s) return '';
  const rx = /\b(connector(?:s)?|connections?)\s*(?:for|type|of)?\s*([a-z0-9._\- ]{2,})$/i;
  const m = s.match(rx);
  if (m && m[2]) return m[2].trim();
  const slash = s.match(/^\/(?:connections?|connectors?)\s+(.+)$/i);
  if (slash && slash[1]) return slash[1].trim();
  return '';
}

// unified caller (normal / resume)
async function callFlowise({
  question,
  humanInput,
  authToken,
  workflowSelection,
  workflowId,
  connectorSelection,
  connectorIdSelection,
  projectSelection,
  projectId
}) {
  const sessionId = getSessionId();
  const vars = {
    authToken,
    ...(workflowSelection ? { workflowSelection } : {}),
    ...(workflowId ? { workflowId } : {}),
    ...(connectorSelection ? { connectorSelection } : {}),
    ...(connectorIdSelection ? { connectorIdSelection } : {}),
    ...(projectSelection ? { projectSelection } : {}),
    ...(projectId ? { projectId } : {}),
    ...(humanInput?.feedback
      ? {
          workflowSelection: humanInput.feedback,
          connectorSelection: humanInput.feedback,
          connectorIdSelection: humanInput.feedback,
          projectSelection: humanInput.feedback,
        }
      : {}),
  };

  const body = {
    ...(humanInput ? {} : { question }),
    ...(humanInput ? { humanInput } : {}),
    overrideConfig: { sessionId, vars },
  };

  const resp = await fetch(FLOW_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Status code ${resp.status}`);
  return resp.json();
}

// --- extract HITL action from any Flowise build shape ---
function extractHumanInputAction(data) {
  if (data && data.humanInputAction && data.humanInputAction.id) {
    const conds = data.conditions || [];
    const expectedType =
      conds.find((c) => (c.type || '').toLowerCase() === 'proceed')?.type ||
      conds[0]?.type ||
      'proceed';
    return {
      id: data.humanInputAction.id,
      startNodeId: data.humanInputAction.startNodeId || null,
      expectedType,
      conditions: conds,
    };
  }

  const nodes = data?.agentFlowExecutedData || [];
  const hiNode = [...nodes].reverse().find(
    (n) =>
      n?.nodeId === 'humanInputAgentflow_0' ||
      (n?.nodeLabel || '').toLowerCase().includes('human') ||
      (n?.data?.id || '').toLowerCase().includes('humaninput')
  );

  const nested =
    hiNode?.data?.output?.humanInputAction || hiNode?.output?.humanInputAction;
  if (nested && nested.id) {
    const conds =
      hiNode?.data?.output?.conditions || hiNode?.output?.conditions || [];
    const expectedType =
      conds.find((c) => (c.type || '').toLowerCase() === 'proceed')?.type ||
      conds[0]?.type ||
      'proceed';

    return {
      id: nested.id,
      startNodeId: hiNode?.nodeId || hiNode?.data?.id || null,
      expectedType,
      conditions: conds,
    };
  }

  const text = (data?.text || '').toLowerCase();
  if (
    text.includes('please reply with the connector type') ||
    text.includes('please reply with the workflow id')
  ) {
    return {
      id: 'synthetic-human-input',
      startNodeId: 'humanInputAgentflow_0',
      expectedType: 'proceed',
      conditions: [{ type: 'proceed', label: 'Proceed' }],
    };
  }
  return null;
}

// Normalize and strip a leading echo of the user's text
function stripLeadingEcho(botText, userText) {
  if (!botText) return botText || '';
  const bt = String(botText).replace(/\r\n/g, '\n');
  const ut = String(userText || '').trim();
  if (!ut) return bt;

  if (bt.trimStart().toLowerCase().startsWith(ut.toLowerCase())) {
    const after = bt.trimStart().slice(ut.length);
    return after.replace(/^(\s*<br\s*\/>|\s*\n)+/i, '').trimStart();
  }
  const htmlEcho = new RegExp(
    '^\\s*<p>\\s*' + ut.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*<\\/p>\\s*',
    'i'
  );
  if (htmlEcho.test(bt)) return bt.replace(htmlEcho, '').trimStart();
  return bt;
}

function collapseExcessBlankLines(s) {
  return String(s || '')
    .replace(/(?:\s*<br\s*\/>\s*){3,}/gi, '<br /><br />')
    .replace(/(?:\s*\n\s*){3,}/g, '\n\n')
    .trim();
}

// Helper to get text length from HTML (approximate)
function getTextLength(html) {
  if (!html) return 0;
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return (tempDiv.textContent || tempDiv.innerText || '').length;
  } catch {
    // Fallback: strip HTML tags and count
    return html.replace(/<[^>]*>/g, '').length;
  }
}

// Truncate HTML content at word boundary
function truncateHtml(html, maxLength = 300) {
  if (!html) return '';
  const textLength = getTextLength(html);
  if (textLength <= maxLength) return html;
  
  // Strip HTML to get plain text for finding word boundary
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  
  // Find a good truncation point (word boundary)
  let truncateAt = maxLength;
  const lastSpace = plainText.lastIndexOf(' ', maxLength);
  if (lastSpace > maxLength * 0.7) truncateAt = lastSpace;
  
  // Simple approach: truncate the HTML string and add ellipsis
  // This preserves basic formatting
  let charCount = 0;
  let inTag = false;
  let truncated = '';
  
  for (let i = 0; i < html.length; i++) {
    const char = html[i];
    
    if (char === '<') {
      inTag = true;
      truncated += char;
    } else if (char === '>') {
      inTag = false;
      truncated += char;
    } else if (!inTag) {
      if (charCount >= truncateAt) {
        truncated += '...';
        break;
      }
      truncated += char;
      charCount++;
    } else {
      truncated += char;
    }
  }
  
  return truncated;
}

const HelpdeskFloatingIcon = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const isMedium = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [openChat, setOpenChat] = useState(false);
  const chatRef = useRef(null);

  // Font size helper - reduce for md and lg screens
  const getFontSize = (baseSize) => {
    if (isMedium || isLarge) {
      // Reduce by ~10% for medium and large screens
      const size = parseFloat(baseSize);
      return `${size * 0.9}rem`;
    }
    return baseSize;
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(null);

  // HITL state
  const [awaitingHumanInput, setAwaitingHumanInput] = useState(false);
  const [humanFeedback, setHumanFeedback] = useState('');
  const [humanActionId, setHumanActionId] = useState(null);
  const [humanStartNodeId, setHumanStartNodeId] = useState(null);
  const [expectedActionType, setExpectedActionType] = useState('proceed');
  const [hitlConditions, setHitlConditions] = useState([]);

  // --- NEW: scroll management ---
  const messagesRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const shouldAutoScrollRef = useRef(true);


  // Expanded messages state for read more/less
  const [expandedMessages, setExpandedMessages] = useState(new Set());

  const computeIsAtBottom = () => {
    const el = messagesRef.current;
    if (!el) return true;
    const threshold = 24; // px tolerance
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const handleScroll = () => {
    const atBottom = computeIsAtBottom();
    setIsAtBottom(atBottom);
    shouldAutoScrollRef.current = atBottom;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };
  // --- END NEW ---

  // Save messages to localStorage
  const saveMessagesToStorage = (msgs) => {
    try {
      localStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(msgs));
    } catch (e) {
      console.warn('Failed to save chat history', e);
    }
  };

  // Clear chat
  const clearChat = () => {
    const welcomeMsg = { 
      sender: 'bot', 
      text: 'Hello, Shoot your query!',
      timestamp: new Date()
    };
    setMessages([welcomeMsg]);
    saveMessagesToStorage([welcomeMsg]);
    toast.success('Chat cleared', { position: 'top-right', autoClose: 2000 });
  };

  // Copy message
  const copyMessage = (text, messageIdx) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    const plainText = tempDiv.textContent || tempDiv.innerText || text;
    
    navigator.clipboard.writeText(plainText).then(() => {
      setCopiedMessageId(messageIdx);
      toast.success('Message copied!', { position: 'top-right', autoClose: 2000 });
      setTimeout(() => setCopiedMessageId(null), 2000);
    }).catch(() => {
      toast.error('Failed to copy', { position: 'top-right', autoClose: 2000 });
    });
    setMessageMenuAnchor(null);
  };

  // Open message menu
  const handleMessageMenuOpen = (event, messageIdx) => {
    event.stopPropagation();
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessageIndex(messageIdx);
  };

  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessageIndex(null);
  };

  // Load from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(CHAT_STORAGE_KEY);
    if (savedState === 'true') setOpenChat(true);
    
    // Load saved messages
    try {
      const savedMessages = localStorage.getItem(MESSAGES_STORAGE_KEY);
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to load chat history', e);
    }
  }, []);

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(CHAT_STORAGE_KEY, openChat.toString());
    if (openChat) {
      // ensure bottom on open
      setTimeout(() => {
        scrollToBottom('auto');
        setIsAtBottom(true);
        shouldAutoScrollRef.current = true;
      }, 0);
    }
  }, [openChat]);

  // Add startup message when chat opens
  useEffect(() => {
    if (openChat && messages.length === 0) {
      const welcomeMsg = { 
        sender: 'bot', 
        text: 'Hello, Shoot your query!',
        timestamp: new Date()
      };
      setMessages([welcomeMsg]);
      saveMessagesToStorage([welcomeMsg]);
    }
  }, [openChat, messages.length]);

  // Keep view pinned to bottom only if user was already at bottom
  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      scrollToBottom(messages.length <= 2 ? 'auto' : 'smooth');
    }
    setTimeout(() => setIsAtBottom(computeIsAtBottom()), 0);
  }, [messages]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on menu items or menu itself
      if (
        event.target.closest('.MuiMenu-root') ||
        event.target.closest('.MuiMenuItem-root') ||
        event.target.closest('.MuiPaper-root')
      ) {
        return;
      }
      if (
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        !event.target.closest('#helpdesk-floating-icon')
      ) {
        setOpenChat(false);
      }
    };
    if (openChat) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openChat]);

  const toggleChat = () => setOpenChat((prev) => !prev);

  const safeHtml = (html) => DOMPurify.sanitize(html || '');

  const pushBot = (rawHtml) => {
    const html = (rawHtml && rawHtml.trim()) ? rawHtml.replace(/\n/g, '<br />') : 'No response from chatbot.';
    const newMessage = { 
      sender: 'bot', 
      text: safeHtml(html),
      timestamp: new Date()
    };
    setMessages((prev) => {
      const updated = [...prev, newMessage];
      saveMessagesToStorage(updated);
      return updated;
    });
  };

  const sendMessage = async () => {
    if (isSending) return;
    const trimmed = (input || '').trim();
    if (!trimmed) return;

    const authToken = Cookies.get('authToken');
    const connectorSelection = extractConnectorSelection(trimmed);

    setInput('');
    setIsSending(true);

    const userMessage = { 
      sender: 'user', 
      text: safeHtml(trimmed),
      timestamp: new Date()
    };
    setMessages((prev) => {
      const updated = [...prev, userMessage];
      saveMessagesToStorage(updated);
      return updated;
    });
    shouldAutoScrollRef.current = true;

    try {
      const data = await callFlowise({
        question: trimmed,
        authToken,
        connectorSelection,
      });

      const cleaned = collapseExcessBlankLines(stripLeadingEcho(data.text, trimmed));
      pushBot(cleaned);

      const action = extractHumanInputAction(data);
      if (action && action.id) {
        setAwaitingHumanInput(true);
        setHumanActionId(action.id);
        setHumanStartNodeId(action.startNodeId || 'humanInputAgentflow_0');
        setExpectedActionType(action.expectedType || 'proceed');
        setHitlConditions(Array.isArray(action.conditions) ? action.conditions : []);
      } else {
        setAwaitingHumanInput(false);
        setHumanActionId(null);
        setHumanFeedback('');
        setHumanStartNodeId(null);
        setExpectedActionType('proceed');
        setHitlConditions([]);
      }
    } catch (err) {
      console.error('Flowise API call failed', err);
      const errorMsg = err.message?.includes('timeout') || err.message?.includes('network')
        ? 'Network error. Please check your connection and try again.'
        : 'Unable to connect to the assistant. Please try again in a moment.';
      pushBot(errorMsg);
      toast.error('Failed to send message', { position: 'top-right', autoClose: 3000 });
    } finally {
      setIsSending(false);
    }
  };

  const submitHumanInput = async (chosenType) => {
    if (!humanActionId) return;

    const authToken = Cookies.get('authToken');
    const type = chosenType || expectedActionType || 'proceed';
    const feedback = (humanFeedback || '').trim();

    shouldAutoScrollRef.current = true;

    try {
      const data = await callFlowise({
        humanInput: {
          id: humanActionId,
          type,
          feedback,
          startNodeId: humanStartNodeId || 'humanInputAgentflow_0',
        },
        authToken,
        workflowSelection: feedback || undefined,
        connectorSelection: feedback || undefined,
        connectorIdSelection: feedback || undefined,
      });

      const cleaned = collapseExcessBlankLines(stripLeadingEcho(data.text, feedback));
      pushBot(cleaned);

      setAwaitingHumanInput(false);
      setHumanActionId(null);
      setHumanFeedback('');
      setHumanStartNodeId(null);
      setExpectedActionType('proceed');
      setHitlConditions([]);
    } catch (err) {
      console.error('Flowise resume failed', err);
      pushBot('Error resuming after approval.');
    }
  };

  // const pickAction = (text) => {
  //   setInput(text);
  //   // Auto-focus the input field
  //   setTimeout(() => {
  //     const inputEl = document.querySelector('input[placeholder="Type your message..."]');
  //     if (inputEl) inputEl.focus();
  //   }, 0);
  // };

  // Suggestions for quick actions
  const suggestions = [
    'show my workflows',
    'run workflow',
    'show my connections',
    'show my projects',
    'delete workflow by ID',
    'delete connection by ID',
    'what is Jira integration?',
    'open help center',
    'contact support',
    'How can I schedule Workflow?',
     'What is connection',
     'where can I check logs?',
     'List transformation functions',
     'Which datatype it will support?',
  ];

  return (
    <>
      <Tooltip
        title="Need help? Click to chat!"
        arrow
        placement="left"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'rgb(99, 114, 201)',
              color: '#fff',
              fontSize: '0.875rem',
              fontFamily: 'Inter, sans-serif',
              padding: '8px 12px',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '& .MuiTooltip-arrow': {
                color: 'rgb(99, 114, 201)',
              },
            },
          },
        }}
      >
        <Box
          id="helpdesk-floating-icon"
          sx={{
            position: 'fixed',
            bottom: isMobile ? 16 : 24,
            right: isMobile ? 16 : 24,
            width: isMobile ? 56 : 64,
            height: isMobile ? 56 : 64,
            borderRadius: '50%',
            boxShadow: '0px 6px 20px 0px rgba(0, 0, 0, 0.35), 0px 0px 0px 2px rgba(255, 255, 255, 0.1) inset',
            background: 'linear-gradient(135deg, rgb(99, 114, 201) 0%, rgb(48, 58, 114) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1300,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              transform: 'scale(1.1) translateY(-2px)',
              boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.4), 0px 0px 0px 3px rgba(255, 255, 255, 0.15) inset',
              background: 'linear-gradient(135deg, rgb(110, 125, 211) 0%, rgb(48, 58, 114) 100%)',
            },
            '&:active': {
              transform: 'scale(1.05) translateY(0px)',
              boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.3)',
            },
          }}
          onClick={toggleChat}
        >
          <Box
            component="img"
            src={helpdeskIcon}
            alt="Helpdesk"
            sx={{
              width: isMobile ? 24 : 28,
              height: isMobile ? 24 : 28,
              filter: 'brightness(0) invert(1)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'rotate(5deg) scale(1.05)',
              },
            }}
          />
        </Box>
      </Tooltip>

      <Slide direction="up" in={openChat} mountOnEnter unmountOnExit>
        <Box
          ref={chatRef}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 90,
            right: isMobile ? 12 : 24,
            left: isMobile ? 12 : 'auto',
            zIndex: 1299,
            width: isMobile ? 'calc(100% - 24px)' : isTablet ? 400 : 460,
            height: isMobile 
              ? 'calc(100vh - 100px)' 
              : isTablet 
                ? 'calc(100vh - 120px)' 
                : 620,
            maxHeight: isMobile ? 'calc(100vh - 100px)' : isTablet ? 'calc(100vh - 120px)' : '80vh',
            borderRadius: 1.7,
            overflow: 'hidden',
            boxShadow: '0px 6px 20px 0px rgba(0, 0, 0, 0.35), 0px 0px 0px 2px rgba(255, 255, 255, 0.1) inset',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'linear-gradient(to Top, #ffffff 0%,rgb(220, 222, 233) 100%)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              p: 1.2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'rgb(126, 139, 196)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={helpdeskIcon}
                alt="Helpdesk"
                sx={{ width: 24, height: 24}}
              />
              <Box sx={{ fontSize: getFontSize('0.875rem'), fontWeight: 600, color: '#ffffff', fontFamily: 'Inter, sans-serif' }}>
                AI Assistant
              </Box>
            </Box>
            <Tooltip title="Clear chat" arrow>
              <IconButton
                size="medium"
                onClick={clearChat}
                sx={{
                  color: '#ffffff',
                  '&:hover': {
                    color: '#ef4444',
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            ref={messagesRef}
            onScroll={handleScroll}
            sx={{ flex: 1, p: 2, overflowY: 'auto', position: 'relative' }}
          >
            {messages.map((m, idx) => {
              const isExpanded = expandedMessages.has(idx);
              const textLength = getTextLength(m.text);
              const isLongMessage = textLength > 300;
              const displayText = isLongMessage && !isExpanded 
                ? truncateHtml(m.text, 300)
                : m.text;

              const toggleExpand = () => {
                setExpandedMessages((prev) => {
                  const next = new Set(prev);
                  if (next.has(idx)) {
                    next.delete(idx);
                  } else {
                    next.add(idx);
                  }
                  return next;
                });
              };

              return (
                <Box 
                  key={idx} 
                  sx={{ 
                    textAlign: m.sender === 'user' ? 'right' : 'left', 
                    mb: 1.5,
                    position: 'relative',
                    '&:hover .message-actions': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: m.sender === 'user' ? 'rgb(66, 81, 165)' : 'rgb(239, 238, 238)',
                      border: m.sender === 'user' ? '1px solid rgb(48, 59, 122)' : '1px solid rgb(211, 211, 211)',
                      color: m.sender === 'user' ? '#fff' : '#000',
                      wordBreak: 'break-word',
                      maxWidth: '85%',
                      position: 'relative',
                    }}
                  >
                    <Box
                      dangerouslySetInnerHTML={{ __html: displayText }}
                      sx={{
                        '& p': { margin: 0, marginBottom: '0.5em' },
                        '& p:last-child': { marginBottom: 0 },
                      }}
                    />
                    {isLongMessage && (
                      <Box
                        onClick={toggleExpand}
                        sx={{
                          display: 'block',
                          mt: 1.5,
                          pt: 0.5,
                          cursor: 'pointer',
                          fontSize: getFontSize('0.8125rem'),
                          fontWeight: 600,
                          color: m.sender === 'user' ? 'rgba(255, 255, 255, 0.85)' : '#5965A6',
                          textDecoration: 'none',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderTop: m.sender === 'user' 
                            ? '1px solid rgba(255, 255, 255, 0.15)' 
                            : '1px solid rgba(0, 0, 0, 0.08)',
                          fontFamily: 'Inter, sans-serif',
                          letterSpacing: '0.2px',
                          '&:hover': {
                            color: m.sender === 'user' ? '#fff' : '#4a5588',
                            textDecoration: 'underline',
                            transform: 'translateX(2px)',
                          },
                          '&:active': {
                            transform: 'translateX(1px)',
                          },
                          userSelect: 'none',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: m.sender === 'user' 
                              ? 'rgba(255, 255, 255, 0.5)' 
                              : '#5965A6',
                            opacity: 0.6,
                            transition: 'opacity 0.2s ease',
                          },
                          '&:hover::before': {
                            opacity: 1,
                          },
                          pl: 1.5,
                        }}
                      >
                        {isExpanded ? '▼ Read less' : '▶ Read more'}
                      </Box>
                    )}
                    {/* Timestamp */}
                    <Box
                      sx={{
                        fontSize: getFontSize('0.7rem'),
                        color: m.sender === 'user' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
                        mt: 0.5,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {m.timestamp ? formatTime(new Date(m.timestamp)) : ''}
                    </Box>
                  </Box>
                  {/* Message Actions */}
                  {m.sender === 'bot' && (
                    <IconButton
                      className="message-actions"
                      size="small"
                      onClick={(e) => handleMessageMenuOpen(e, idx)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: m.sender === 'user' ? 'auto' : -8,
                        right: m.sender === 'user' ? -8 : 'auto',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        width: 24,
                        height: 24,
                        '&:hover': {
                          bgcolor: '#fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: '0.875rem', color: '#5965A6' }} />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Message Actions Menu */}
          <Menu
            anchorEl={messageMenuAnchor}
            open={Boolean(messageMenuAnchor)}
            onClose={handleMessageMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 160,
                boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
              },
            }}
          >
            <MenuItem
              onClick={() => {
                if (selectedMessageIndex !== null && messages[selectedMessageIndex]) {
                  copyMessage(messages[selectedMessageIndex].text, selectedMessageIndex);
                }
              }}
              sx={{ fontSize: getFontSize('0.875rem'), fontFamily: 'Inter, sans-serif' }}
            >
              {copiedMessageId === selectedMessageIndex ? (
                <>
                  <CheckIcon sx={{ fontSize: getFontSize('1rem'), mr: 1, color: '#10b981' }} />
                  Copied
                </>
              ) : (
                <>
                  <ContentCopyIcon sx={{ fontSize: getFontSize('1rem'), mr: 1, color: '#5965A6' }} />
                  Copy message
                </>
              )}
            </MenuItem>
          </Menu>

          <Fade in={openChat && !isAtBottom}>
            <IconButton
              onClick={() => scrollToBottom('smooth')}
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 72,
                zIndex: 2,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
                '&:hover': { bgcolor: '#f9fafb' },
              }}
              size="large"
              aria-label="Scroll to bottom"
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Fade>

          <Fade in={isSending}>
            <Box sx={{ px: 2, pb: 1, color: '#6b7280', fontSize: getFontSize('0.75rem') }}>
              Bot is typing…
            </Box>
          </Fade>

          {awaitingHumanInput && (
            <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 1.5, flexDirection: 'column', backgroundColor: '#f9fafb' }}>
              <Box sx={{ fontSize: getFontSize('0.875rem'), color: '#374151', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                The assistant needs additional info. Please provide it below.
              </Box>
              <TextField
                multiline
                minRows={3}
                placeholder='Provide the required input here...'
                value={humanFeedback}
                onChange={(e) => setHumanFeedback(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: getFontSize('0.875rem'),
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '0.5rem',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease-in-out',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5965A6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: getFontSize('0.875rem'),
                    fontFamily: 'Inter, sans-serif',
                    color: '#1f2937',
                    padding: '10px 14px',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                      fontSize: getFontSize('0.875rem'),
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {hitlConditions && hitlConditions.length > 0 ? (
                  hitlConditions.map((c) => (
                    <Button
                      key={c.type}
                      size="small"
                      variant="contained"
                      onClick={() => submitHumanInput(c.type)}
                      sx={{
                        bgcolor: '#5965A6',
                        color: '#fff',
                        fontSize: getFontSize('0.875rem'),
                        textTransform: 'none',
                        boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
                        '&:hover': {
                          bgcolor: '#4a5588',
                        },
                      }}
                    >
                      {c.label || c.type}
                    </Button>
                  ))
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => submitHumanInput('proceed')}
                    sx={{
                      bgcolor: '#5965A6',
                      color: '#fff',
                      fontSize: getFontSize('0.875rem'),
                      textTransform: 'none',
                      boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
                      '&:hover': {
                        bgcolor: '#4a5588',
                      },
                    }}
                  >
                    Proceed
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Suggestions Chips Area */}
          <Box
            sx={{
              px: 1,
              pt: 1,
              pb: 0.5,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#f9fafb',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e1',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#94a3b8',
                  },
                },
                pb: 0.5,
              }}
            >
              {suggestions.map((suggestion, idx) => (
                <Chip
                  key={idx}
                  label={suggestion}
                  onClick={() => {
                    setInput(suggestion);
                    // Auto-focus the input field
                    setTimeout(() => {
                      const inputEl = document.querySelector('input[placeholder="Type your message..."]');
                      if (inputEl) inputEl.focus();
                    }, 0);
                  }}
                  sx={{
                    fontSize: getFontSize('0.75rem'),
                    height: '28px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: '#ffffff',
                    color: '#5965A6',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#5965A6',
                      color: '#ffffff',
                      borderColor: '#5965A6',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(89, 101, 166, 0.2)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    flexShrink: 0,
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={input}
              placeholder="Type your message..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: getFontSize('0.875rem'),
                  fontFamily: 'Inter, sans-serif',
                  borderRadius: '0.5rem',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease-in-out',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#5965A6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5965A6',
                    borderWidth: '2px',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f9fafb',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: getFontSize('0.875rem'),
                  fontFamily: 'Inter, sans-serif',
                  color: '#1f2937',
                  padding: '8.5px 14px',
                  '&::placeholder': {
                    color: '#9ca3af',
                    opacity: 1,
                    fontSize: getFontSize('0.875rem'),
                  },
                },
              }}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={isSending}
            />
            <Button
              onClick={sendMessage}
              size="medium"
              sx={{
                ml: 1,
                bgcolor: '#5965A6',
                color: '#fff',
                fontSize: getFontSize('0.875rem'), 
                boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
                '&:hover': {
                  bgcolor: '#4a5588',
                },
                '&:disabled': {
                  bgcolor: '#ccc',
                  color: '#666',
                },
              }}
              variant="contained"
              disabled={isSending}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Slide>
    </>
  );
};

export default HelpdeskFloatingIcon;
